import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, CheckCircle, Building, Mail, Phone, MapPin, User, Shield, 
  ArrowLeft, ArrowRight, Loader, Eye, EyeOff, AlertTriangle, Globe,
  Lock, Check, X, Info, Star, Clock, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import your existing BusinessApiService
import BusinessApiService from '../../services/BusinessApiService';

// =====================================================
// TYPES THAT EXACTLY MATCH YOUR BACKEND
// =====================================================
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
}

// EXACT structure your backend expects
interface BusinessRegistrationPayload {
  business: {
    companyName: string;
    tradingName?: string;
    registrationNumber?: string;
    taxId?: string;
    industry: string;
    businessType: string;
    website?: string;
    phone: string;
    email: string;
    description?: string;
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
    contactPerson: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      position: string;
    };
    // GS1 — optional
    gs1Prefix?: string;
    gs1MemberNumber?: string;
  };
  businessUser: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    position: string;
    role: string;
    isAdmin: boolean;
    userType: string;
  };
  agreeToTerms: boolean;
}

interface FormData {
  companyName: string;
  tradingName: string;
  registrationNumber: string;
  taxId: string;
  industry: string;
  businessType: string;
  description: string;
  website: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  position: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPosition: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
  // GS1 fields — optional, Phase 1
  gs1Prefix: string;
  gs1MemberNumber: string;
  hasOwnGs1: boolean;
}

// =====================================================
// REMOVE LOCAL API CLIENT - USE YOUR BusinessApiService INSTEAD
// =====================================================

// =====================================================
// PASSWORD STRENGTH COMPONENT
// =====================================================
const PasswordStrengthIndicator: React.FC<{ password: string }> = ({ password }) => {
  const getStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = getStrength(password);
  const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  
  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            className={`h-1 flex-1 rounded transition-colors ${
              i <= strength ? colors[strength - 1] || 'bg-gray-200' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <p className={`text-xs mt-1 transition-colors ${
        strength >= 4 ? 'text-green-600' : strength >= 3 ? 'text-blue-600' : 'text-orange-600'
      }`}>
        Password strength: {labels[strength - 1] || 'Very Weak'}
      </p>
    </div>
  );
};

// =====================================================
// FORM INPUT COMPONENT
// =====================================================
const FormInput: React.FC<{
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  icon?: React.ReactNode;
  statusIcon?: React.ReactNode;
  hint?: string;
  disabled?: boolean;
}> = ({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  error, 
  placeholder, 
  required, 
  icon, 
  statusIcon,
  hint,
  disabled = false
}) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
            icon ? 'pl-10' : ''
          } ${
            statusIcon ? 'pr-10' : ''
          } ${
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
          } ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
          placeholder={placeholder}
        />
        {statusIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {statusIcon}
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-600 text-sm mt-1 flex items-center">
          <AlertTriangle className="w-3 h-3 mr-1" />
          {error}
        </p>
      )}
      {hint && !error && (
        <p className="text-gray-500 text-xs mt-1">{hint}</p>
      )}
    </div>
  );
};

// =====================================================
// MAIN REGISTRATION COMPONENT
// =====================================================
const BusinessRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  
  // Real-time validation states
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [available, setAvailable] = useState<Record<string, boolean | undefined>>({});
  
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    tradingName: '',
    registrationNumber: '',
    taxId: '',
    industry: '',
    businessType: '',
    // GS1
    gs1Prefix: '',
    gs1MemberNumber: '',
    hasOwnGs1: false,
    description: '',
    website: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    position: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPosition: 'Business Owner',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  // Availability checking that matches your backend
  const checkAvailability = useCallback(async (field: string, value: string): Promise<void> => {
    if (!value.trim()) return;
    
    // TEMPORARILY DISABLED - Your backend doesn't have this endpoint yet
    console.log(`Would check availability for ${field}: ${value}`);
    
    // For now, assume everything is available
    setAvailable(prev => ({ ...prev, [field]: true }));
    
    return; // Skip actual API call for now
    
    /* UNCOMMENT WHEN YOU ADD THE ENDPOINT TO YOUR BACKEND:
    setChecking(prev => ({ ...prev, [field]: true }));
    
    try {
      let checkData: any = {};
      
      if (field === 'email') {
        checkData.email = value;
      } else if (field === 'adminEmail') {
        checkData.adminEmail = value;
      } else if (field === 'companyName') {
        checkData.companyName = value;
      }
      
      const result = await apiClient.checkAvailability(checkData);
      
      if (result.success && result.data?.available) {
        const availability = result.data.available;
        
        if (field === 'email') {
          setAvailable(prev => ({ ...prev, [field]: availability.businessEmail }));
        } else if (field === 'adminEmail') {
          setAvailable(prev => ({ ...prev, [field]: availability.adminEmail }));
        } else if (field === 'companyName') {
          setAvailable(prev => ({ ...prev, [field]: availability.companyName }));
        }
      }
    } catch (error) {
      console.error(`Error checking ${field} availability:`, error);
      setAvailable(prev => ({ ...prev, [field]: undefined }));
    } finally {
      setChecking(prev => ({ ...prev, [field]: false }));
    }
    */
  }, []);

  // Debounced effects for availability checking
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (formData.email && /\S+@\S+\.\S+/.test(formData.email)) {
        checkAvailability('email', formData.email);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [formData.email, checkAvailability]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (formData.adminEmail && /\S+@\S+\.\S+/.test(formData.adminEmail)) {
        checkAvailability('adminEmail', formData.adminEmail);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [formData.adminEmail, checkAvailability]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (formData.companyName && formData.companyName.length >= 2) {
        checkAvailability('companyName', formData.companyName);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [formData.companyName, checkAvailability]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1: // Business Information
        if (!formData.companyName.trim()) {
          newErrors.companyName = 'Company name is required';
        } else if (formData.companyName.length < 2) {
          newErrors.companyName = 'Company name must be at least 2 characters';
        } else if (available.companyName === false) {
          newErrors.companyName = 'This company name is already registered';
        }
        
        if (!formData.industry) {
          newErrors.industry = 'Please select an industry';
        }

        if (!formData.businessType) {
          newErrors.businessType = 'Please select your business type';
        }
        
        if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) {
          newErrors.website = 'Website must be a valid URL (include http:// or https://)';
        }
        break;
        
      case 2: // Contact & Address
        if (!formData.email.trim()) {
          newErrors.email = 'Business email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        } else if (available.email === false) {
          newErrors.email = 'This email is already registered';
        }
        
        if (!formData.phone.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (formData.phone.replace(/\D/g, '').length < 10) {
          newErrors.phone = 'Please enter a valid phone number (at least 10 digits)';
        }
        
        if (!formData.street.trim()) newErrors.street = 'Street address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';
        if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal code is required';
        if (!formData.contactFirstName.trim()) newErrors.contactFirstName = 'Contact first name is required';
        if (!formData.contactLastName.trim()) newErrors.contactLastName = 'Contact last name is required';
        
        if (!formData.contactEmail.trim()) {
          newErrors.contactEmail = 'Contact email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
          newErrors.contactEmail = 'Please enter a valid contact email';
        }
        
        if (!formData.contactPhone.trim()) {
          newErrors.contactPhone = 'Contact phone is required';
        } else if (formData.contactPhone.replace(/\D/g, '').length < 10) {
          newErrors.contactPhone = 'Please enter a valid contact phone number';
        }
        
        if (!formData.position.trim()) newErrors.position = 'Position is required';
        break;
        
      case 3: // Admin User & Security
        if (!formData.adminFirstName.trim()) newErrors.adminFirstName = 'Admin first name is required';
        if (!formData.adminLastName.trim()) newErrors.adminLastName = 'Admin last name is required';
        
        if (!formData.adminEmail.trim()) {
          newErrors.adminEmail = 'Admin email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
          newErrors.adminEmail = 'Please enter a valid admin email';
        } else if (available.adminEmail === false) {
          newErrors.adminEmail = 'This admin email is already registered';
        }
        
        if (!formData.adminPosition.trim()) newErrors.adminPosition = 'Admin position is required';
        
        if (!formData.password) {
          newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)) {
          newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
        }
        
        if (!formData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        
        if (!formData.agreeToTerms) {
          newErrors.agreeToTerms = 'You must agree to the Terms of Service and Privacy Policy';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setSubmitError('');

    if (field === 'email' || field === 'adminEmail' || field === 'companyName') {
      setAvailable(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNext = (): void => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = (): void => {
    setCurrentStep(prev => prev - 1);
  };

  // SUBMIT FUNCTION - NOW USES YOUR BusinessApiService
  const handleSubmit = async (): Promise<void> => {
    if (!validateStep(3)) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      console.log('🚀 Starting business registration...');
      
      // Create payload in the format your backend expects
      const registrationPayload: BusinessRegistrationPayload = {
        business: {
          companyName: formData.companyName,
          tradingName: formData.tradingName || undefined,
          registrationNumber: formData.registrationNumber || undefined,
          taxId: formData.taxId || undefined,
          industry: formData.industry,
          businessType: formData.businessType,
          website: formData.website || undefined,
          phone: formData.phone,
          email: formData.email,
          //description: undefined,
          description: formData.description || undefined,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            postalCode: formData.postalCode,
            country: formData.country
          },
          contactPerson: {
            firstName: formData.contactFirstName,
            lastName: formData.contactLastName,
            email: formData.contactEmail,
            phone: formData.contactPhone,
            position: formData.position
          },
          // GS1 — only send if business has own prefix
          ...(formData.hasOwnGs1 && formData.gs1Prefix ? {
            gs1Prefix: formData.gs1Prefix.trim(),
            gs1MemberNumber: formData.gs1MemberNumber?.trim() || undefined,
          } : {})
        },
        businessUser: {
          firstName: formData.adminFirstName,
          lastName: formData.adminLastName,
          email: formData.adminEmail,
          password: formData.password,
          position: formData.adminPosition,
          role: 'business_owner',
          isAdmin: true,
          userType: 'business_user'
        },
        agreeToTerms: formData.agreeToTerms
      };

      // Use YOUR BusinessApiService
      const response = await BusinessApiService.registerBusiness(registrationPayload);
      
      console.log('📨 Registration response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Registration successful!');
        
        // Store all the data returned by your backend
        localStorage.setItem('businessRegistration', JSON.stringify({
          businessId: response.data.businessId,
          userId: response.data.userId,
          submittedAt: new Date().toISOString()
        }));

        // Store auth data if provided
        if (response.data.token) {
          BusinessApiService.setBusinessToken(response.data.token);
          localStorage.setItem('userType', 'business_user');
        }
        
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        
        if (response.data.user) {
          BusinessApiService.setBusinessData(response.data.user);
        }
        
        if (response.data.business) {
          localStorage.setItem('business', JSON.stringify(response.data.business));
        }
        
        setSubmitSuccess(true);
      } else {
        throw new Error(response.message || 'Registration failed. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      
      // Enhanced error handling that matches your backend error codes
      const errorMappings: Record<string, string> = {
        'MISSING_BUSINESS_TYPE': 'Please select your business type',
        'INVALID_BUSINESS_TYPE': 'Invalid business type selected',
        'BUSINESS_EXISTS': 'A business with this email or company name already exists',
        'USER_EXISTS': 'A user with this admin email already exists',
        'MISSING_STRUCTURE': 'Please fill in all required fields',
        'MISSING_BUSINESS_DATA': 'Please fill in all business information',
        'MISSING_ADDRESS': 'Please fill in complete business address',
        'MISSING_CONTACT': 'Please fill in complete contact person information',
        'MISSING_USER_DATA': 'Please fill in all admin user information',
        'INVALID_BUSINESS_EMAIL': 'Please enter a valid business email',
        'INVALID_USER_EMAIL': 'Please enter a valid admin email',
        'WEAK_PASSWORD': 'Password does not meet security requirements',
        'VALIDATION_ERROR': 'Please check your information and try again',
        'REGISTRATION_FAILED': 'Registration failed. Please try again.',
        'DUPLICATE_ENTRY': 'This information is already registered',
        'NETWORK_ERROR': 'Network error. Please check your connection and try again'
      };
      
      const errorMessage = errorMappings[error.code] || 
                          error.message || 
                          'An unexpected error occurred. Please try again.';
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldStatus = (field: string): React.ReactNode => {
    if (checking[field]) return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
    if (available[field] === true) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (available[field] === false) return <X className="w-4 h-4 text-red-500" />;
    return null;
  };

  const industries = [
    'Agriculture & Food',
    'Automotive',
    'Beauty & Personal Care',
    'Clothing & Fashion',
    'Electronics',
    'Healthcare & Medical',
    'Home & Garden',
    'Industrial & Manufacturing',
    'Jewelry & Accessories',
    'Pharma & Supplements',
    'Sports & Fitness',
    'Toys & Games',
    'Other'
  ];

  const countries = [
    'United States', 'Canada', 'Mexico', 'Australia', 
    'China', 'France', 'Japan', 'Singapore', 'Vietnam','Other'
  ];

  // Success screen
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your business registration has been submitted successfully. Your account is pending approval.
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-sm font-medium text-blue-800 mb-2">Next Steps:</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li className="flex items-center">
                      <Clock className="w-3 h-3 mr-2" />
                      Verify your email address
                    </li>
                    <li className="flex items-center">
                      <FileText className="w-3 h-3 mr-2" />
                      Wait for admin approval (1-3 business days)
                    </li>
                    <li className="flex items-center">
                      <Star className="w-3 h-3 mr-2" />
                      Access your business dashboard
                    </li>
                    <li className="flex items-center">
                      <User className="w-3 h-3 mr-2" />
                      Start activating your tags
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/business/pending-approval', { replace: true })}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Check Application Status
              </button>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold">Register Your Business</h1>
              <div className="bg-white/20 rounded-lg px-3 py-1">
                <span className="text-sm font-medium">JepTags Platform</span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div>
              <div className="flex items-center">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      step < currentStep ? 'bg-green-500 text-white' :
                      step === currentStep ? 'bg-white text-blue-600' :
                      'bg-white/30 text-white'
                    }`}>
                      {step < currentStep ? <Check className="w-4 h-4" /> : step}
                    </div>
                    {step < 3 && (
                      <div className={`flex-1 h-1 mx-3 rounded transition-colors ${
                        step < currentStep ? 'bg-green-500' : 'bg-white/30'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex justify-between text-xs mt-2 opacity-90">
                <span>Business Info</span>
                <span>Contact & Address</span>
                <span>Admin Account</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="mx-8 mt-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
              <div className="flex">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Registration Failed</h3>
                  <p className="text-sm text-red-700 mt-1">{submitError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="px-8 py-6">
            {currentStep === 1 && (
              <div>
                <div className="flex items-center mb-6">
                  <Building className="w-6 h-6 text-blue-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Business Information</h2>
                </div>
                
                <div className="space-y-6">
  <FormInput
    label="Company Name"
    value={formData.companyName}
    onChange={(value) => handleInputChange('companyName', value)}
    error={errors.companyName}
    placeholder="Enter your company name"
    required
    icon={<Building className="w-4 h-4" />}
    statusIcon={getFieldStatus('companyName')}
    hint={available.companyName === true ? "✓ Company name is available" : undefined}
  />

  <FormInput
    label="Trading Name (if different)"
    value={formData.tradingName}
    onChange={(value) => handleInputChange('tradingName', value)}
    placeholder="Enter trading name (optional)"
    icon={<Building className="w-4 h-4" />}
  />

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <FormInput
      label="Registration Number"
      value={formData.registrationNumber}
      onChange={(value) => handleInputChange('registrationNumber', value)}
      placeholder="Business registration number"
      icon={<FileText className="w-4 h-4" />}
    />

    <FormInput
      label="Tax ID / EIN"
      value={formData.taxId}
      onChange={(value) => handleInputChange('taxId', value)}
      placeholder="Tax identification number"
      icon={<FileText className="w-4 h-4" />}
    />
  </div>

  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Industry <span className="text-red-500">*</span>
    </label>
    <select
      value={formData.industry}
      onChange={(e) => handleInputChange('industry', e.target.value)}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        errors.industry ? 'border-red-500' : 'border-gray-300'
      }`}
    >
      <option value="">Select your industry</option>
      {industries.map(industry => (
        <option key={industry} value={industry}>{industry}</option>
      ))}
    </select>
    {errors.industry && (
      <p className="text-red-600 text-sm mt-1 flex items-center">
        <AlertTriangle className="w-3 h-3 mr-1" />
        {errors.industry}
      </p>
    )}
  </div>

  {/* Business Type Selector */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Business Type <span className="text-red-500">*</span>
    </label>
    <p className="text-xs text-gray-500 mb-3">
      Select the role that best describes how your business operates in the supply chain.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {[
        { value: 'manufacturer', icon: '🏭', label: 'Manufacturer / Factory', desc: 'You produce or manufacture products' },
        { value: 'distributor', icon: '🚚', label: 'Distributor', desc: 'You distribute products between factories and stores' },
        { value: 'wholesaler', icon: '🏪', label: 'Wholesale Dealer', desc: 'You buy in bulk and sell to retailers' },
        { value: 'retailer', icon: '🛒', label: 'Retailer / Store', desc: 'You sell products directly to consumers' },
        { value: 'brand', icon: '🏷️', label: 'Brand Owner', desc: 'You own the brand, may outsource manufacturing' },
        { value: 'other', icon: '🔧', label: 'Other', desc: 'Other type of business' },
      ].map(({ value, icon, label, desc }) => (
        <button
          key={value}
          type="button"
          onClick={() => handleInputChange('businessType', value)}
          className={`flex items-start p-3 rounded-lg border-2 text-left transition-all ${
            formData.businessType === value
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
          }`}
        >
          <span className="text-2xl mr-3 mt-0.5">{icon}</span>
          <div>
            <p className={`text-sm font-semibold ${formData.businessType === value ? 'text-blue-700' : 'text-gray-800'}`}>
              {label}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
          </div>
          {formData.businessType === value && (
            <CheckCircle className="w-4 h-4 text-blue-500 ml-auto mt-0.5 flex-shrink-0" />
          )}
        </button>
      ))}
    </div>
    {errors.businessType && (
      <p className="text-red-600 text-sm mt-2 flex items-center">
        <AlertTriangle className="w-3 h-3 mr-1" />
        {errors.businessType}
      </p>
    )}
  </div>

  <FormInput
    label="Website"
    type="url"
    value={formData.website}
    onChange={(value) => handleInputChange('website', value)}
    error={errors.website}
    placeholder="https://www.yourcompany.com"
    icon={<Globe className="w-4 h-4" />}
  />

  {/* Business Description (textarea for multiline) */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Business Description
    </label>
    <textarea
      value={formData.description}
      onChange={e => handleInputChange('description', e.target.value)}
      placeholder="Briefly describe your business, products, or services"
      rows={4}
      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
    />
    {errors.description && (
      <p className="text-red-600 text-sm mt-1 flex items-center">
        <AlertTriangle className="w-3 h-3 mr-1" />
        {errors.description}
      </p>
    )}
  </div>
</div>
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <div className="flex items-center mb-6">
                  <Mail className="w-6 h-6 text-blue-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Contact & Address Information</h2>
                </div>
                
                <div className="space-y-8">
                  {/* Business Contact */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-4">Business Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormInput
                        label="Business Email"
                        type="email"
                        value={formData.email}
                        onChange={(value) => handleInputChange('email', value)}
                        error={errors.email}
                        placeholder="business@company.com"
                        required
                        icon={<Mail className="w-4 h-4" />}
                        statusIcon={getFieldStatus('email')}
                        hint={available.email === true ? "✓ Email is available" : undefined}
                      />

                      <FormInput
                        label="Business Phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(value) => handleInputChange('phone', value)}
                        error={errors.phone}
                        placeholder="+1 (555) 123-4567"
                        required
                        icon={<Phone className="w-4 h-4" />}
                      />
                    </div>
                  </div>

                  {/* Business Address */}
                  <div>
                    <div className="flex items-center mb-4">
                      <MapPin className="w-5 h-5 text-gray-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-800">Business Address</h3>
                    </div>
                    <div className="space-y-4">
                      <FormInput
                        label="Street Address"
                        value={formData.street}
                        onChange={(value) => handleInputChange('street', value)}
                        error={errors.street}
                        placeholder="123 Business Street"
                        required
                        icon={<MapPin className="w-4 h-4" />}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormInput
                          label="City"
                          value={formData.city}
                          onChange={(value) => handleInputChange('city', value)}
                          error={errors.city}
                          placeholder="City"
                          required
                        />

                        <FormInput
                          label="State"
                          value={formData.state}
                          onChange={(value) => handleInputChange('state', value)}
                          error={errors.state}
                          placeholder="State"
                          required
                        />

                        <FormInput
                          label="Postal Code"
                          value={formData.postalCode}
                          onChange={(value) => handleInputChange('postalCode', value)}
                          error={errors.postalCode}
                          placeholder="12345"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {countries.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Contact Person */}
                  <div>
                    <div className="flex items-center mb-4">
                      <User className="w-5 h-5 text-gray-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-800">Primary Contact Person</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput
                          label="First Name"
                          value={formData.contactFirstName}
                          onChange={(value) => handleInputChange('contactFirstName', value)}
                          error={errors.contactFirstName}
                          placeholder="John"
                          required
                          icon={<User className="w-4 h-4" />}
                        />

                        <FormInput
                          label="Last Name"
                          value={formData.contactLastName}
                          onChange={(value) => handleInputChange('contactLastName', value)}
                          error={errors.contactLastName}
                          placeholder="Doe"
                          required
                          icon={<User className="w-4 h-4" />}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput
                          label="Contact Email"
                          type="email"
                          value={formData.contactEmail}
                          onChange={(value) => handleInputChange('contactEmail', value)}
                          error={errors.contactEmail}
                          placeholder="john.doe@company.com"
                          required
                          icon={<Mail className="w-4 h-4" />}
                        />

                        <FormInput
                          label="Contact Phone"
                          type="tel"
                          value={formData.contactPhone}
                          onChange={(value) => handleInputChange('contactPhone', value)}
                          error={errors.contactPhone}
                          placeholder="+1 (555) 123-4567"
                          required
                          icon={<Phone className="w-4 h-4" />}
                        />
                      </div>

                      <FormInput
                        label="Position"
                        value={formData.position}
                        onChange={(value) => handleInputChange('position', value)}
                        error={errors.position}
                        placeholder="CEO, Manager, Director, etc."
                        required
                        icon={<User className="w-4 h-4" />}
                      />
                    </div>
                  </div>

                  {/* GS1 Information — Only for Manufacturer/Brand */}
                  {(formData.businessType === 'manufacturer' || formData.businessType === 'brand') && (
                  <div className="border border-blue-100 rounded-xl p-6 bg-blue-50">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-3">🏷️</span>
                      <div>
                        <h3 className="text-lg font-medium text-gray-800">GS1 Barcode Information</h3>
                        <p className="text-sm text-gray-500">Optional — only if you already have your own GS1 credentials</p>
                      </div>
                    </div>

                    {/* Toggle */}
                    <div className="flex items-center mb-4">
                      <button
                        type="button"
                        onClick={() => handleInputChange('hasOwnGs1', !formData.hasOwnGs1)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.hasOwnGs1 ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.hasOwnGs1 ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                      <span className="ml-3 text-sm font-medium text-gray-700">
                        I have my own GS1 Company Prefix
                      </span>
                    </div>

                    {formData.hasOwnGs1 && (
                      <div className="space-y-4 mt-4">
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <p className="text-xs text-blue-700 mb-3">
                            ✅ Your own GS1 prefix will be used for all product barcodes — fully compliant with Walmart, Amazon and global retailers.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                GS1 Company Prefix <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.gs1Prefix}
                                onChange={(e) => handleInputChange('gs1Prefix', e.target.value)}
                                placeholder="e.g. 0614142"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                maxLength={10}
                              />
                              <p className="text-xs text-gray-500 mt-1">6-9 digit prefix from your GS1 membership</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                GS1 Member Number
                              </label>
                              <input
                                type="text"
                                value={formData.gs1MemberNumber}
                                onChange={(e) => handleInputChange('gs1MemberNumber', e.target.value)}
                                placeholder="e.g. GS1US-12345"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                              <p className="text-xs text-gray-500 mt-1">Optional — your GS1 membership ID</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!formData.hasOwnGs1 && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200 mt-2">
                        <p className="text-xs text-gray-600">
                          🔹 JepTags will assign an internal GS1 prefix for your products automatically when approved.
                          You can upgrade to your own GS1 prefix later from your dashboard.
                        </p>
                      </div>
                    )}
                  </div>
                  )}
                  {/* Non-manufacturer notice */}
                  {formData.businessType && formData.businessType !== 'manufacturer' && formData.businessType !== 'brand' && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">ℹ️</span>
                        <div>
                          <p className="text-sm font-medium text-gray-700">No GS1 Required</p>
                          <p className="text-xs text-gray-500">
                            As a {formData.businessType}, you don't need a GS1 prefix. 
                            JepTags will handle product traceability automatically.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div>
                <div className="flex items-center mb-6">
                  <Shield className="w-6 h-6 text-blue-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Admin Account Setup</h2>
                </div>
                
                <div className="space-y-8">
                  {/* Admin User Information */}
                  <div>
                    <div className="flex items-center mb-4">
                      <User className="w-5 h-5 text-gray-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-800">Business Administrator</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      This person will have full access to manage the business account and can invite other team members.
                    </p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput
                          label="Admin First Name"
                          value={formData.adminFirstName}
                          onChange={(value) => handleInputChange('adminFirstName', value)}
                          error={errors.adminFirstName}
                          placeholder="John"
                          required
                          icon={<User className="w-4 h-4" />}
                        />

                        <FormInput
                          label="Admin Last Name"
                          value={formData.adminLastName}
                          onChange={(value) => handleInputChange('adminLastName', value)}
                          error={errors.adminLastName}
                          placeholder="Smith"
                          required
                          icon={<User className="w-4 h-4" />}
                        />
                      </div>

                      <FormInput
                        label="Admin Email"
                        type="email"
                        value={formData.adminEmail}
                        onChange={(value) => handleInputChange('adminEmail', value)}
                        error={errors.adminEmail}
                        placeholder="admin@company.com"
                        required
                        icon={<Mail className="w-4 h-4" />}
                        statusIcon={getFieldStatus('adminEmail')}
                        hint={available.adminEmail === true ? "✓ Admin email is available" : "This will be used to log into the business dashboard"}
                      />

                      <FormInput
                        label="Admin Position"
                        value={formData.adminPosition}
                        onChange={(value) => handleInputChange('adminPosition', value)}
                        error={errors.adminPosition}
                        placeholder="Business Owner, CEO, Manager..."
                        required
                        icon={<User className="w-4 h-4" />}
                      />
                    </div>
                  </div>

                  {/* Password Setup */}
                  <div>
                    <div className="flex items-center mb-4">
                      <Lock className="w-5 h-5 text-gray-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-800">Account Security</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.password ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter a strong password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        
                        <PasswordStrengthIndicator password={formData.password} />
                        
                        <div className="mt-2">
                          <div className="text-xs text-gray-600">
                            Password requirements:
                            <ul className="grid grid-cols-2 gap-1 mt-1">
                              <li className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                                {formData.password.length >= 8 ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                8+ characters
                              </li>
                              <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                                {/[A-Z]/.test(formData.password) ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                Uppercase letter
                              </li>
                              <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                                {/[a-z]/.test(formData.password) ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                Lowercase letter
                              </li>
                              <li className={`flex items-center ${/\d/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                                {/\d/.test(formData.password) ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                Number
                              </li>
                              <li className={`flex items-center ${/[@$!%*?&]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'} col-span-2`}>
                                {/[@$!%*?&]/.test(formData.password) ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                Special character (@$!%*?&)
                              </li>
                            </ul>
                          </div>
                        </div>
                        {errors.password && (
                          <p className="text-red-600 text-sm mt-1 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {errors.password}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                            className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.confirmPassword ? 'border-red-500' : 
                              formData.confirmPassword && formData.password === formData.confirmPassword ? 'border-green-500' :
                              'border-gray-300'
                            }`}
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {formData.confirmPassword && formData.password === formData.confirmPassword && (
                          <p className="text-green-600 text-sm mt-1 flex items-center">
                            <Check className="w-3 h-3 mr-1" />
                            Passwords match
                          </p>
                        )}
                        {errors.confirmPassword && (
                          <p className="text-red-600 text-sm mt-1 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                        className={`mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${
                          errors.agreeToTerms ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="text-sm">
                        <span className="text-gray-700">
                          I agree to the{' '}
                          <button type="button" className="text-blue-600 hover:text-blue-800 underline font-medium">
                            Terms of Service
                          </button>{' '}
                          and{' '}
                          <button type="button" className="text-blue-600 hover:text-blue-800 underline font-medium">
                            Privacy Policy
                          </button>, and confirm that all business information provided is accurate.
                        </span>
                        {errors.agreeToTerms && (
                          <p className="text-red-600 text-sm mt-1 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {errors.agreeToTerms}
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                currentStep === 1
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            <div className="text-sm text-gray-500">
              Step {currentStep} of 3
            </div>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating Business...
                  </>
                ) : (
                  <>
                    <Building className="w-4 h-4 mr-2" />
                    Register Business
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessRegistrationPage;